import { HttpMethod, apiRequest } from './api'
import type { ApiResponse } from './api'

// 课程类型
export interface CourseData {
  id: number
  name: string
  cover: string
  introduction: string
  batchName: string
  startTime: string
  endTime: string
  classNum: number | null
  totalNum: number | null
  enrollNum: number | null
  zhuangyuanContent: string | null
  nonZhuangyuanContent: string | null
  nonAgreementContent: string | null
  zhuangyuanId: number
  nonZhuangyuanId: number
  nonAgreementId: number
  sort?: number
  pendingPaymentCount?: number
  pendingContractCount?: number
  pendingAccommodationCount?: number
  pendingClassAllocationCount?: number
  classAllocatedCount?: number
  cancelledCount?: number
  refundedCount?: number
  totalOrderCount?: number
}

// 课程列表项（用于下拉选择）
export interface CourseListItem {
  id: number
  name: string
}

// 课程查询参数
export interface CourseQueryParams {
  name?: string
  batchId?: number
  pageNum?: number
  pageSize?: number
  afterEndTime?: string
}

// 课程创建请求
export interface CourseCreateRequest {
  name: string
  cover?: string | File
  introduction?: string
  batchId: number
  startTime: string
  endTime: string
  zhuangyuanId: number
  nonZhuangyuanId: number
  nonAgreementId: number
  sort?: number
}

// 课程更新请求
export interface CourseUpdateRequest {
  courseId: number
  name?: string
  cover?: string | File
  introduction?: string
  batchId?: number
  startTime?: string
  endTime?: string
  zhuangyuanId?: number
  nonZhuangyuanId?: number
  nonAgreementId?: number
  sort?: number
}

// 课程服务
export class CourseService {
  // 根据条件查询课程（分页）
  static async queryCourses(params: CourseQueryParams): Promise<
    ApiResponse<{
      records: Array<CourseData>
      total: number
      size: number
      current: number
    }>
  > {
    return apiRequest<{
      records: Array<CourseData>
      total: number
      size: number
      current: number
    }>('/course/queryCoursesPageByConditions', {
      method: HttpMethod.POST,
      body: params,
    })
  }

  // 添加课程
  static async createCourse(
    data: CourseCreateRequest,
  ): Promise<ApiResponse<string>> {
    const formData = new FormData()

    // 添加必填字段
    formData.append('name', data.name)
    formData.append('batchId', data.batchId.toString())
    formData.append('startTime', data.startTime)
    formData.append('endTime', data.endTime)
    formData.append('zhuangyuanId', data.zhuangyuanId.toString())
    formData.append('nonZhuangyuanId', data.nonZhuangyuanId.toString())
    formData.append('nonAgreementId', data.nonAgreementId.toString())

    // 添加可选字段
    if (data.introduction) {
      formData.append('introduction', data.introduction)
    }
    if (data.cover) {
      formData.append('cover', data.cover)
    }
    if (data.sort !== undefined) {
      formData.append('sort', data.sort.toString())
    }

    return apiRequest<string>('/course/addCourse', {
      method: HttpMethod.POST,
      body: formData,
    })
  }

  // 更新课程
  static async updateCourse(
    data: CourseUpdateRequest,
  ): Promise<ApiResponse<string>> {
    const formData = new FormData()

    // 添加课程ID
    formData.append('courseId', data.courseId.toString())

    // 添加可选字段
    if (data.name) {
      formData.append('name', data.name)
    }
    if (data.batchId) {
      formData.append('batchId', data.batchId.toString())
    }
    if (data.startTime) {
      formData.append('startTime', data.startTime)
    }
    if (data.endTime) {
      formData.append('endTime', data.endTime)
    }
    if (data.zhuangyuanId) {
      formData.append('zhuangyuanId', data.zhuangyuanId.toString())
    }
    if (data.nonZhuangyuanId) {
      formData.append('nonZhuangyuanId', data.nonZhuangyuanId.toString())
    }
    if (data.nonAgreementId) {
      formData.append('nonAgreementId', data.nonAgreementId.toString())
    }
    if (data.introduction) {
      formData.append('introduction', data.introduction)
    }
    if (data.cover) {
      formData.append('cover', data.cover)
    }
    if (data.sort !== undefined) {
      formData.append('sort', data.sort.toString())
    }

    return apiRequest<string>('/course/updateCourse', {
      method: HttpMethod.POST,
      body: formData,
    })
  }

  // 删除课程
  static async deleteCourse(courseId: number): Promise<ApiResponse<string>> {
    const formData = new FormData()
    formData.append('courseId', courseId.toString())
    // 发送删除请求
    return apiRequest<string>('/course/deleteCourse', {
      method: HttpMethod.POST,
      body: formData,
    })
  }
}

// 查询所有课程（下拉选择使用）
export async function getAllCourses(): Promise<
  ApiResponse<Array<CourseListItem>>
> {
  // 使用查询接口获取所有课程的简化信息
  const response = await CourseService.queryCourses({
    pageNum: 1,
    pageSize: 10000,
  })

  // 转换为下拉选择需要的格式
  const courseListItems: Array<CourseListItem> = response.data.records.map(
    (course: CourseData) => ({
      id: course.id,
      name: course.name,
    }),
  )

  return {
    code: response.code,
    msg: response.msg,
    data: courseListItems,
  }
}
